import { createContext, useContext, useState, useEffect } from 'react'
import { db } from '../firebase'
import { useAuth } from './AuthContext'
import { ORDER_WORKER_URL } from '../config/siteConfig'
import {
  collection,
  addDoc,
  updateDoc,
  deleteDoc,
  doc,
  onSnapshot,
  getDocs,
  getDoc,
  setDoc
} from 'firebase/firestore'

const AppContext = createContext()

// Initial products data - used ONLY the very first time the database is empty
const initialProducts = [
  { 
    name: 'Chicken Breast', 
    price: 550, 
    category: 'chicken', 
    unit: 'per kg', 
    available: true, 
    image: '/Chicken Breast.jpeg'
  },
  { 
    name: 'Whole Chicken', 
    price: 380, 
    category: 'chicken', 
    unit: 'per kg', 
    available: true, 
    image: '/Whole Chicken.jpeg'
  },
  { 
    name: 'Chicken Drumsticks', 
    price: 420, 
    category: 'chicken', 
    unit: 'per kg', 
    available: true, 
    image: '/Chicken Drumsticks.jpeg'
  },
  { 
    name: 'Beef Boneless', 
    price: 1200, 
    category: 'beef', 
    unit: 'per kg', 
    available: true, 
    image: '/Beef Boneless.jpeg'
  },
  { 
    name: 'Beef Qeema', 
    price: 1100, 
    category: 'beef', 
    unit: 'per kg', 
    available: true, 
    image: '/Beef Qeema.jpeg'
  },
  { 
    name: 'Beef Boti', 
    price: 1300, 
    category: 'beef', 
    unit: 'per kg', 
    available: false, 
    image: '/Beef Boti.jpeg'
  },
  { 
    name: 'Tomatoes', 
    price: 80, 
    category: 'vegetables', 
    unit: 'per kg', 
    available: true, 
    image: '/Tomatoes.jpeg'
  },
  { 
    name: 'Potatoes', 
    price: 60, 
    category: 'vegetables', 
    unit: 'per kg', 
    available: true, 
    image: '/Potatoes.jpeg'
  },
  { 
    name: 'Onions', 
    price: 70, 
    category: 'vegetables', 
    unit: 'per kg', 
    available: true, 
    image: '/Onions.jpeg'
  },
  { 
    name: 'Spinach', 
    price: 40, 
    category: 'vegetables', 
    unit: 'per bunch', 
    available: true, 
    image: '/Spinach.jpeg'
  },
  { 
    name: 'Carrots', 
    price: 90, 
    category: 'vegetables', 
    unit: 'per kg', 
    available: true, 
    image: '/Carrots.jpeg'
  },
  { 
    name: 'Capsicum', 
    price: 120, 
    category: 'vegetables', 
    unit: 'per kg', 
    available: true, 
    image: '/Capsicum.jpeg'
  },
  { 
    name: 'Apples', 
    price: 250, 
    category: 'fruits', 
    unit: 'per kg', 
    available: true, 
    image: '/Apple.jpeg'
  },
  { 
    name: 'Bananas', 
    price: 100, 
    category: 'fruits', 
    unit: 'per dozen', 
    available: true, 
    image: 'https://coresg-normal.trae.ai/api/v1/text_to_image?prompt=bananas&image_size=square_hd'
  },
  { 
    name: 'Mangoes', 
    price: 300, 
    category: 'fruits', 
    unit: 'per kg', 
    available: true, 
    image: 'https://coresg-normal.trae.ai/api/v1/text_to_image?prompt=mangoes&image_size=square_hd'
  },
  { 
    name: 'Oranges', 
    price: 180, 
    category: 'fruits', 
    unit: 'per kg', 
    available: true, 
    image: 'https://coresg-normal.trae.ai/api/v1/text_to_image?prompt=oranges&image_size=square_hd'
  }
]

// Default label settings - used only the very first time the labelSettings
// document doesn't exist yet in Firestore.
const DEFAULT_LABEL_SETTINGS = {
  shopName: 'Akhtar Sabir Fresh',
  logoUrl: '/Brand Logo.png',
  visibleFields: {
    productName: true,
    weight: true,
    pricePerKg: true,
    totalPrice: true,
    barcode: true,
    qrCode: true,
    packagingDate: true,
    expiryDate: true,
    batchNumber: true,
    shopName: true,
    logo: true
  },
  shelfLifeDaysByCategory: { chicken: 2, beef: 3, vegetables: 5, fruits: 5 },
  defaultShelfLifeDays: 3,
  scaleUnit: 'auto',
  labelSize: { widthMm: 50, heightMm: 30 },
  tspl2Enabled: false
}

// Default payment settings - "Online Payment" stays hidden at checkout
// until the shop owner fills in real bank details and explicitly enables
// it, so customers never see placeholder/fake account info.
const DEFAULT_PAYMENT_SETTINGS = {
  bankName: '',
  accountTitle: '',
  accountNumber: '',
  iban: '',
  onlinePaymentEnabled: false
}

export const AppProvider = ({ children }) => {
  const { currentUser } = useAuth()
  const [products, setProducts] = useState([])
  const [orders, setOrders] = useState([])
  const [deletedItems, setDeletedItems] = useState([])
  const [productDetails, setProductDetails] = useState([])
  const [blogPosts, setBlogPosts] = useState([])
  const [labelPrints, setLabelPrints] = useState([])
  const [labelSettings, setLabelSettings] = useState(null)
  const [paymentSettings, setPaymentSettings] = useState(null)
  const [loading, setLoading] = useState(true)
  // Separate from `loading` above, which actually only tracks the orders
  // listener (see below) - pages that read products/blogPosts directly
  // (Shop, Home, ProductDetail, Blog, BlogPost) need their own signal,
  // since `loading` can flip false before these listeners' first snapshot
  // arrives, showing a false "not found" for a page that just hasn't
  // loaded yet.
  const [productsLoaded, setProductsLoaded] = useState(false)
  const [blogPostsLoaded, setBlogPostsLoaded] = useState(false)
  const [newOrder, setNewOrder] = useState(null)

  const playNotificationSound = () => {
    try {
      const audioContext = new (window.AudioContext || window.webkitAudioContext)()
      const oscillator = audioContext.createOscillator()
      const gainNode = audioContext.createGain()
      oscillator.connect(gainNode)
      gainNode.connect(audioContext.destination)
      oscillator.frequency.value = 800
      oscillator.type = 'sine'
      gainNode.gain.setValueAtTime(0.3, audioContext.currentTime)
      gainNode.gain.exponentialRampToValueAtTime(0.01, audioContext.currentTime + 1)
      oscillator.start(audioContext.currentTime)
      oscillator.stop(audioContext.currentTime + 1)
    } catch (error) {
      console.error('Failed to play notification sound:', error)
    }
  }

  // One-time setup: if the products collection is empty (brand new database),
  // fill it with the starter product list so the shop isn't empty.
  useEffect(() => {
    const seedProductsIfEmpty = async () => {
      const snapshot = await getDocs(collection(db, 'products'))
      if (snapshot.empty) {
        for (const product of initialProducts) {
          await addDoc(collection(db, 'products'), product)
        }
      }
    }
    seedProductsIfEmpty()
  }, [])

  // Live connection to the "products" collection.
  // Runs automatically whenever ANY device adds/edits/deletes a product.
  useEffect(() => {
    const unsubscribe = onSnapshot(collection(db, 'products'), (snapshot) => {
      const productsData = snapshot.docs.map(docSnap => ({
        id: docSnap.id,
        ...docSnap.data()
      }))
      setProducts(productsData)
      setProductsLoaded(true)
    })
    return () => unsubscribe()
  }, [])

  // Live connection to the "orders" collection - admin-only, like
  // deletedItems/labelSettings/labelPrints below. This used to subscribe
  // unconditionally for every visitor, which meant every anonymous
  // storefront session held open a realtime connection to every order
  // ever placed (name, phone, address, delivery OTP) - a real privacy
  // exposure on top of the wasted bandwidth/CPU, since Firestore rules
  // had allowed public read on the whole collection to make this work.
  // The rules now only allow public `get` (Track Order's single-ID
  // lookup), so this listener would fail with permission-denied for an
  // anonymous visitor anyway - gating it here just avoids firing it.
  useEffect(() => {
    if (!currentUser) {
      setOrders([])
      setLoading(false)
      return
    }
    let previousOrderIds = new Set()
    const unsubscribe = onSnapshot(collection(db, 'orders'), (snapshot) => {
      const ordersData = snapshot.docs.map(docSnap => ({
        id: docSnap.id,
        ...docSnap.data()
      }))
      ordersData.sort((a, b) => new Date(b.orderDate) - new Date(a.orderDate))
      
      const currentOrderIds = new Set(ordersData.map(o => o.id))
      const newOrderIds = [...currentOrderIds].filter(id => !previousOrderIds.has(id))
      
      if (previousOrderIds.size > 0 && newOrderIds.length > 0) {
        const newOrders = ordersData.filter(o => newOrderIds.includes(o.id))
        setNewOrder(newOrders[0])
        playNotificationSound()
      }
      
      previousOrderIds = currentOrderIds
      setOrders(ordersData)
      setLoading(false)
    })
    return () => unsubscribe()
  }, [currentUser])

  // Live connection to the "deletedItems" (trash) collection - admin-only
  // per firestore.rules, and only ever read on admin pages, so this only
  // subscribes once an admin is actually logged in. Subscribing
  // unconditionally for every anonymous visitor would just generate a
  // permission-denied error on every page load.
  useEffect(() => {
    if (!currentUser) {
      setDeletedItems([])
      return
    }
    const unsubscribe = onSnapshot(collection(db, 'deletedItems'), (snapshot) => {
      const trashData = snapshot.docs.map(docSnap => ({
        trashId: docSnap.id,
        ...docSnap.data()
      }))
      setDeletedItems(trashData)
    })
    return () => unsubscribe()
  }, [currentUser])

  // Live connection to productDetails collection
  useEffect(() => {
    const unsubscribe = onSnapshot(collection(db, 'productDetails'), (snapshot) => {
      const productDetailsData = snapshot.docs.map(docSnap => ({
        id: docSnap.id,
        ...docSnap.data()
      }))
      setProductDetails(productDetailsData)
    })
    return () => unsubscribe()
  }, [])

  // Live connection to blogPosts collection
  useEffect(() => {
    const unsubscribe = onSnapshot(collection(db, 'blogPosts'), (snapshot) => {
      const blogData = snapshot.docs.map(docSnap => ({
        id: docSnap.id,
        ...docSnap.data()
      }))
      setBlogPosts(blogData)
      setBlogPostsLoaded(true)
    })
    return () => unsubscribe()
  }, [])

  // One-time setup: create the labelSettings/config document if it doesn't
  // exist yet, same idea as seedProductsIfEmpty above. labelSettings is
  // admin-only per firestore.rules, so this (like the two effects below)
  // only runs once an admin is logged in - running it for every anonymous
  // visitor would just fail with permission-denied.
  useEffect(() => {
    if (!currentUser) return
    const seedLabelSettingsIfMissing = async () => {
      const snap = await getDoc(doc(db, 'labelSettings', 'config'))
      if (!snap.exists()) {
        await setDoc(doc(db, 'labelSettings', 'config'), DEFAULT_LABEL_SETTINGS)
      }
    }
    seedLabelSettingsIfMissing()
  }, [currentUser])

  // Live connection to the labelSettings/config singleton document.
  useEffect(() => {
    if (!currentUser) {
      setLabelSettings(null)
      return
    }
    const unsubscribe = onSnapshot(doc(db, 'labelSettings', 'config'), (snap) => {
      if (snap.exists()) setLabelSettings({ id: snap.id, ...snap.data() })
    })
    return () => unsubscribe()
  }, [currentUser])

  // Live connection to the labelPrints (in-shop label print audit log) collection.
  useEffect(() => {
    if (!currentUser) {
      setLabelPrints([])
      return
    }
    const unsubscribe = onSnapshot(collection(db, 'labelPrints'), (snapshot) => {
      const data = snapshot.docs.map(docSnap => ({
        id: docSnap.id,
        ...docSnap.data()
      }))
      data.sort((a, b) => new Date(b.timestamp) - new Date(a.timestamp))
      setLabelPrints(data)
    })
    return () => unsubscribe()
  }, [currentUser])

  // One-time setup: create the paymentSettings/config document if missing.
  useEffect(() => {
    const seedPaymentSettingsIfMissing = async () => {
      const snap = await getDoc(doc(db, 'paymentSettings', 'config'))
      if (!snap.exists()) {
        await setDoc(doc(db, 'paymentSettings', 'config'), DEFAULT_PAYMENT_SETTINGS)
      }
    }
    seedPaymentSettingsIfMissing()
  }, [])

  // Live connection to the paymentSettings/config singleton document.
  useEffect(() => {
    const unsubscribe = onSnapshot(doc(db, 'paymentSettings', 'config'), (snap) => {
      if (snap.exists()) setPaymentSettings({ id: snap.id, ...snap.data() })
    })
    return () => unsubscribe()
  }, [])

  // ---------------- Product management ----------------

  const addProduct = async (product) => {
    await addDoc(collection(db, 'products'), product)
  }

  const editProduct = async (id, updatedProduct) => {
    await updateDoc(doc(db, 'products', id), updatedProduct)
  }

  const deleteProduct = async (id) => {
    const productToDelete = products.find(p => p.id === id)
    if (productToDelete) {
      await addDoc(collection(db, 'deletedItems'), {
        item: productToDelete,
        type: 'product',
        time: Date.now()
      })
      await deleteDoc(doc(db, 'products', id))
    }
  }

  const undoDelete = async (deletedItem) => {
    if (deletedItem.type === 'product') {
      const { id, ...itemData } = deletedItem.item
      await addDoc(collection(db, 'products'), itemData)
    } else if (deletedItem.type === 'order') {
      const { id, ...itemData } = deletedItem.item
      await addDoc(collection(db, 'orders'), itemData)
    }
    await deleteDoc(doc(db, 'deletedItems', deletedItem.trashId))
  }

  // ---------------- Product details and Blog management ----------------

  const addProductDetail = async (detail) => {
    return addDoc(collection(db, 'productDetails'), detail)
  }
  
  const editProductDetail = async (id, updatedDetail) => {
    return updateDoc(doc(db, 'productDetails', id), updatedDetail)
  }
  
  const addBlogPost = async (post) => {
    return addDoc(collection(db, 'blogPosts'), post)
  }
  
  const editBlogPost = async (id, updatedPost) => {
    return updateDoc(doc(db, 'blogPosts', id), updatedPost)
  }
  
  const deleteBlogPost = async (id) => {
    return deleteDoc(doc(db, 'blogPosts', id))
  }

  // ---------------- Order management ----------------

  // Orders are validated and created server-side by a Cloudflare Worker
  // (worker/src/index.js), which recomputes the total from live Firestore
  // prices instead of trusting whatever the browser sends - the price/
  // total fields in orderData are only used for the pre-checkout display,
  // not for what actually gets charged/stored. The Worker returns the
  // same { id, ...order } shape this function used to build itself, so
  // nothing else in the app needs to change.
  const placeOrder = async (orderData) => {
    const response = await fetch(ORDER_WORKER_URL, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(orderData)
    })
    if (!response.ok) {
      const body = await response.json().catch(() => ({}))
      throw new Error(body.error || 'Failed to place order')
    }
    return response.json()
  }

  const markDelivered = async (orderId, enteredOTP) => {
    const order = orders.find(o => o.id === orderId)
    if (order && order.deliveryOTP && order.deliveryOTP !== enteredOTP) {
      throw new Error('Incorrect OTP. Please confirm the code with the customer.')
    }
    await updateDoc(doc(db, 'orders', orderId), { status: 'Delivered' })
  }

  const editOrder = async (orderId, updatedOrder) => {
    const { id, ...updateData } = updatedOrder
    await updateDoc(doc(db, 'orders', orderId), updateData)
  }

  const deleteOrder = async (orderId) => {
    const orderToDelete = orders.find(o => o.id === orderId)
    if (orderToDelete) {
      await addDoc(collection(db, 'deletedItems'), {
        item: orderToDelete,
        type: 'order',
        time: Date.now()
      })
      await deleteDoc(doc(db, 'orders', orderId))
    }
  }

  const clearTrash = async () => {
    // Delete all documents in deletedItems collection
    for (const item of deletedItems) {
      await deleteDoc(doc(db, 'deletedItems', item.trashId))
    }
  }

  const clearDeliveredOrders = async () => {
    // Delete all orders with status 'Delivered'
    const deliveredOrders = orders.filter(o => o.status === 'Delivered')
    for (const order of deliveredOrders) {
      await deleteDoc(doc(db, 'orders', order.id))
    }
  }

  const clearNewOrder = () => setNewOrder(null)

  // ---------------- Label printing (In-Shop Sales) management ----------------

  const addLabelPrint = async (entry) => {
    return addDoc(collection(db, 'labelPrints'), entry)
  }

  const updateLabelSettings = async (updatedSettings) => {
    return setDoc(
      doc(db, 'labelSettings', 'config'),
      { ...updatedSettings, updatedAt: new Date().toISOString() },
      { merge: true }
    )
  }

  const updatePaymentSettings = async (updatedSettings) => {
    return setDoc(
      doc(db, 'paymentSettings', 'config'),
      { ...updatedSettings, updatedAt: new Date().toISOString() },
      { merge: true }
    )
  }

  return (
    <AppContext.Provider value={{
      products,
      orders,
      deletedItems,
      productDetails,
      blogPosts,
      labelPrints,
      labelSettings,
      paymentSettings,
      loading,
      productsLoaded,
      blogPostsLoaded,
      newOrder,
      clearNewOrder,
      addProduct,
      editProduct,
      deleteProduct,
      undoDelete,
      placeOrder,
      markDelivered,
      editOrder,
      deleteOrder,
      clearTrash,
      clearDeliveredOrders,
      addProductDetail,
      editProductDetail,
      addBlogPost,
      editBlogPost,
      deleteBlogPost,
      addLabelPrint,
      updateLabelSettings,
      updatePaymentSettings
    }}>
      {children}
    </AppContext.Provider>
  )
}

export const useApp = () => useContext(AppContext)